import { computed, inject } from '@angular/core';
import type { ActivatedRouteSnapshot } from '@angular/router';
import { Router } from '@angular/router';
import { DxTelemetryService } from '@core/dx/dx-telemetry.service';
import { EnvironmentService } from '@core/services/environment.service';
import { ImpersonationService } from '@core/services/impersonation.service';
import type { DxStatusContributor } from '@kerberos-compliance/compliance-shared-ui/dx';
import { dxFormatCountdown, DxRefreshService, DxStatusVariantMap } from '@kerberos-compliance/compliance-shared-ui/dx';
import { AuthFacade } from '@/auth/facades/auth.facade';
import { InactivityService } from '@/auth/services/inactivity.service';
import { AuthStore } from '@/auth/store/auth.store';
import { AuthSourceLabelMap, DeviceIdScopeLabelMap } from '@/auth/types/auth.types';
import { getDeviceIdScopeFromToken, getRawAuthSourceFromToken } from '@/auth/utils/auth-source.utilities';

/**
 * Flow states carried in the snapshot. Enough to correlate, short enough to paste.
 */
const dxSnapshotRecordLimit = 10;

/**
 * Collects the role requirements declared on the active route chain.
 *
 * @param {Router} router - The app's router.
 * @returns {string[]} Role names required to reach the current route.
 */
function currentRouteRoles(router: Router): string[] {
  const roles = new Set<string>();
  let route: ActivatedRouteSnapshot | null = router.routerState.snapshot.root;

  while (route) {
    const declared: unknown = route.data['roles'];
    if (Array.isArray(declared)) {
      for (const role of declared) {
        roles.add(String(role));
      }
    }
    route = route.firstChild;
  }

  return [...roles];
}

/**
 * Build, deployment and route facts — "which build is this, against which backend?"
 *
 * This is the former Build & Environment Badge panel, contributed rather than rendered by
 * a panel of its own. Admin Desk and the onOffice integration already answered this
 * question as a status contributor, so a third app rendering it in a bespoke panel meant
 * the same facts appeared in two shapes and only one of them reached the copyable
 * snapshot.
 *
 * @returns {DxStatusContributor} The build section for the shared status panel.
 */
export function dxBuildStatusContributor(): DxStatusContributor {
  const environmentService = inject(EnvironmentService);
  const router = inject(Router);
  const refresh = inject(DxRefreshService);

  return {
    id: 'build',
    label: 'Build',
    order: 1,
    sections: computed(() => {
      // `Router.url` is not a signal, so without this the section would cache the route
      // the drawer was first opened on - and get pasted into a ticket as the route the
      // problem happened on.
      refresh.token();

      return [
        {
          id: 'environment',
          label: 'Environment',
          rows: [
            { key: 'Target', value: environmentService.target },
            { key: 'Version', value: environmentService.version },
            { key: 'Build type', value: environmentService.isProduction ? 'Production' : 'Development' },
            { key: 'Tenant', value: environmentService.tenantId, isCode: true },
            { key: 'Route', value: router.url, isCode: true },
          ],
          data: {
            version: environmentService.version,
            target: environmentService.target,
            tenantId: environmentService.tenantId,
            route: { url: router.url, requiredRoles: currentRouteRoles(router) },
          },
        },
        {
          id: 'endpoints',
          label: 'Endpoints',
          rows: [
            { key: 'ADP host', value: environmentService.baseAdpHost, isCode: true },
            { key: 'Hanko API', value: environmentService.hankoApiUrl, isCode: true },
            { key: 'Admin Desk', value: environmentService.adminDeskBaseUrl, isCode: true },
            { key: 'Maintenance info', value: environmentService.maintenanceInfoUrl ?? 'Not configured', isCode: true },
          ],
        },
      ];
    }),
  };
}

/**
 * Who this session is and what it may do — as identifiers, never as PII.
 *
 * No email and no display name: this snapshot is pasted into tickets, and under an
 * impersonation the identity on screen is the customer's.
 *
 * @returns {DxStatusContributor} The auth section for the shared status panel.
 */
export function dxAuthStatusContributor(): DxStatusContributor {
  const authStore = inject(AuthStore);
  const authFacade = inject(AuthFacade);
  const impersonationService = inject(ImpersonationService);

  return {
    id: 'auth',
    label: 'Auth',
    order: 2,
    sections: computed(() => {
      const grants = authStore.ownGrants() ?? [];
      const impersonation = impersonationService.context();
      const scopedGrantCount = grants.filter((grant) => Boolean(grant.organizationId)).length;
      const accessToken = authStore.authTokens().accessTokenKerberos;
      const deviceIdScope = getDeviceIdScopeFromToken(accessToken);
      const authSource = getRawAuthSourceFromToken(accessToken);
      const isHankoSession = authStore.isUserLoggedIn() && authFacade.isHankoSession();

      return [
        {
          id: 'context',
          label: 'Session context',
          rows: [
            {
              key: 'Logged in',
              value: authStore.isUserLoggedIn() ? 'Yes' : 'No',
              variant: authStore.isUserLoggedIn() ? DxStatusVariantMap.Success : DxStatusVariantMap.Warning,
            },
            { key: 'Organization', value: authStore.organizationId() ?? 'None', isCode: true },
            { key: 'Industry', value: authStore.organizationIndustry() ?? 'None' },
            { key: 'Roles', value: authStore.roleDisplayNames().join(', ') || 'None' },
            { key: 'Grants', value: `${grants.length} (${scopedGrantCount} org-scoped)` },
            {
              key: 'Impersonating',
              value: impersonation.active ? 'Active' : 'None',
              variant: impersonation.active ? DxStatusVariantMap.Danger : DxStatusVariantMap.Default,
            },
            // The two claims that pick the device cookie. The cookie itself is HttpOnly and never visible here;
            // a missing scope means a token from before the claim existed, which the next refresh rejects.
            {
              key: 'Desk (dids)',
              value: deviceIdScope ? `${DeviceIdScopeLabelMap[deviceIdScope]} (${deviceIdScope})` : 'None',
              variant: deviceIdScope ? DxStatusVariantMap.Default : DxStatusVariantMap.Warning,
              isCode: true,
            },
            {
              key: 'Provider (asrc)',
              value:
                authSource === undefined ? 'None' : `${AuthSourceLabelMap[authSource] ?? 'Unknown'} (${authSource})`,
              isCode: true,
            },
            { key: 'Hanko session', value: isHankoSession ? 'Yes' : 'No' },
          ],
          data: {
            isUserLoggedIn: authStore.isUserLoggedIn(),
            deviceIdScope,
            authSource,
            isHankoSession,
            organizationId: authStore.organizationId() ?? undefined,
            organizationIndustry: authStore.organizationIndustry() ?? undefined,
            roles: authStore.roleDisplayNames(),
            grantCount: grants.length,
            scopedGrantCount,
            // Presence only. The context carries the return token that ends the
            // impersonation, and a snapshot gets pasted into tickets.
            impersonation: { active: impersonation.active, hasReturnToken: Boolean(impersonation.returnToken) },
          },
        },
      ];
    }),
  };
}

/**
 * Why the session is about to end, or ended.
 *
 * @returns {DxStatusContributor} The session section for the shared status panel.
 */
export function dxSessionStatusContributor(): DxStatusContributor {
  const inactivityService = inject(InactivityService);

  return {
    id: 'session',
    label: 'Session',
    order: 3,
    sections: computed(() => [
      {
        id: 'inactivity',
        label: 'Inactivity',
        rows: [
          {
            key: 'Tracking',
            value: inactivityService.isTracking() ? 'Active' : 'Inactive',
            variant: inactivityService.isTracking() ? DxStatusVariantMap.Success : DxStatusVariantMap.Default,
          },
          { key: 'Remaining', value: dxFormatCountdown(inactivityService.remainingMs()) },
          {
            key: 'Warning',
            value: inactivityService.isWarningVisible() ? 'Visible' : 'Normal',
            variant: inactivityService.isWarningVisible() ? DxStatusVariantMap.Warning : DxStatusVariantMap.Default,
          },
        ],
        data: {
          isTracking: inactivityService.isTracking(),
          isWarningVisible: inactivityService.isWarningVisible(),
          remainingMs: inactivityService.remainingMs(),
          sessionExpiryTimestamp: inactivityService.sessionExpiryTimestamp() ?? undefined,
          lastActivityAt: inactivityService.lastActivityAt() ?? undefined,
        },
      },
    ]),
  };
}

/**
 * The login path, which is the one view of it we have: the Hanko SDK bypasses the app's
 * interceptor over `XMLHttpRequest`, so nothing else records these transitions.
 *
 * Contributed rather than rendered by the flow panel alone, so the same buffer reaches
 * the snapshot that gets pasted into a ticket.
 *
 * @returns {DxStatusContributor} The flow section for the shared status panel.
 */
export function dxFlowStatusContributor(): DxStatusContributor {
  const telemetryService = inject(DxTelemetryService);

  return {
    id: 'flow',
    label: 'Login flow',
    order: 4,
    sections: computed(() => {
      const records = telemetryService.flowRecords().slice(0, dxSnapshotRecordLimit);

      return [
        {
          id: 'recent-states',
          label: 'Recent states',
          rows: [
            { key: 'Recorded', value: String(records.length) },
            { key: 'Latest state', value: records[0]?.stateName ?? 'None this session' },
          ],
          data: records.map((record) => ({
            timestamp: record.timestamp,
            flowName: record.flowName,
            stateName: record.stateName,
            previousAction: record.previousAction,
            errorCode: record.errorCode,
          })),
        },
      ];
    }),
  };
}
